import custOfetch from '~/composables/useCustomFetch'
import type { AdminSettings } from '~/types/prisma'

export interface CreateAdminSettingDto {
  description: string
  status?: boolean
  value?: number
  date?: Date
}

export enum AdminSetting {
  EnableAuctionItems = 'enableAuctionItems',
  YearNumber = 'yearNumber',
  YearName = 'yearName',
  EnableTeamChanges = 'enableTeamChanges',
  SelectYear = 'selectYear',
  MaintenanceMode = 'maintenanceMode',
  EnableRaffles = 'enableRaffles',
  DefaultEvent = 'defaultEvent',
  EnableLeaderboard = 'enableLeaderboard',
  CurrentYear = 'currentYear'
}

export interface UpdateAdminSettingDto extends Partial<CreateAdminSettingDto> { }

// Global state for maintenance mode
const globalIsMaintenanceMode = ref(false)
let eventListenerAdded = false

export const useAdminSettings = () => {
  const config = useRuntimeConfig()

  // Setup event listener only once
  if (import.meta.client && !eventListenerAdded) {
    window.addEventListener('maintenanceModeStatus', (event: any) => {
      globalIsMaintenanceMode.value = event.detail.status
    })
    eventListenerAdded = true

    // Initialize maintenance mode status on first use
    const initializeMaintenanceMode = async () => {
      try {
        const maintenanceModeSetting = await custOfetch(`${config.public.apiBase}/admin-settings/description/maintenanceMode`, {
          method: 'GET',
        }, false) as AdminSettings
        globalIsMaintenanceMode.value = maintenanceModeSetting?.status === true
      } catch (error) {
        globalIsMaintenanceMode.value = true
        console.warn('Failed to initialize maintenance mode status:', error)
      }
    }
    initializeMaintenanceMode()
  }

  const getAll = async () => {
    const settings = await custOfetch(`${config.public.apiBase}/admin-settings`, {
      method: 'GET',
    }, false) as AdminSettings[]

    globalIsMaintenanceMode.value = settings.find(setting => setting.description === 'maintenanceMode')?.status === true
    return settings
  }

  const create = async (
    payload: CreateAdminSettingDto,
  ) => {
    return await custOfetch(`${config.public.apiBase}/admin-settings`, {
      method: 'POST',
      body: payload,
    }, false)
  }

  const getById = async (id: string) => {
    return await custOfetch(`${config.public.apiBase}/admin-settings/${id}`, {
      method: 'GET',
    }, false) as AdminSettings
  }

  const getByDescription = async (description: string) => {
    return await custOfetch(`${config.public.apiBase}/admin-settings/description/${description}`, {
      method: 'GET',
    }, false) as AdminSettings
  }

  const updateById = async (
    id: string,
    payload: UpdateAdminSettingDto,
  ) => {
    return await custOfetch(`${config.public.apiBase}/admin-settings/${id}`, {
      method: 'PATCH',
      body: payload,
    }, false) as AdminSettings
  }

  const deleteById = async (id: string) => {
    return await custOfetch(`${config.public.apiBase}/admin-settings/${id}`, {
      method: 'DELETE',
    }, false)
  }

  const getMaintenanceMode = async () => {
    try {
      return await getByDescription(AdminSetting.MaintenanceMode);
    } catch (error) {
      return { status: true }
    }
  }

  return {
    isMaintenanceMode: readonly(globalIsMaintenanceMode),
    getMaintenanceMode,
    create,
    getAll,
    getById,
    getByDescription,
    updateById,
    deleteById,
  }
}
